import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CustomField, CustomFieldWithValue } from '../../../models/custom-fields.model';
import { CustomFieldRendererService } from '../../../services/custom-field-renderer.service';
import { TranslocoService } from '@ngneat/transloco';

/**
 * Editable Custom Field Renderer Component
 * Renders custom fields in edit mode with form controls
 * Follows the project's design system and Material Design principles
 */
@Component({
  selector: 'app-editable-custom-field-renderer',
  templateUrl: './editable-custom-field-renderer.component.html',
  styleUrls: ['./editable-custom-field-renderer.component.scss'],
})
export class EditableCustomFieldRendererComponent implements OnInit, OnDestroy, OnChanges {
  @Input() field!: CustomFieldWithValue;
  @Input() form!: FormGroup;
  @Input() disabled = false;
  @Input() isEditing = false;
  @Input() loadingState = false;

  @Output() onEditCustomField = new EventEmitter<{ fieldName: string; value: any }>();

  private _destroy$ = new Subject<void>();
  fieldConfig: any;

  constructor(
    private fieldRenderer: CustomFieldRendererService,
    private translateService: TranslocoService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Ensure form is available before proceeding
    if (!this.form) {
      console.warn('Form is not available');
      return;
    }

    // Ensure field is available before proceeding
    if (!this.field) {
      console.warn('Field is not available');
      return;
    }

    this.fieldConfig = this.fieldRenderer.prepareFieldConfig(
      this.field,
      this.form.get(this.field.name)?.value,
      this.disabled,
    );

    // Handle disabled state through FormControl instead of template attribute
    this.updateDisabledState();

    // Trigger change detection to ensure the field is properly rendered
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-initialize if form or field changes
    if ((changes['form'] || changes['field']) && this.form && this.field) {
      this.ngOnInit();
    }

    if (
      (changes['disabled'] && !changes['disabled'].firstChange) ||
      (changes['loadingState'] && !changes['loadingState'].firstChange)
    ) {
      this.updateDisabledState();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Updates the disabled state of the form control
   */
  private updateDisabledState(): void {
    const control = this.formControl;
    if (control) {
      // Disable if explicitly disabled, loading, or if field is marked as read_only
      if (this.disabled || this.loadingState || this.field?.read_only) {
        control.disable();
      } else {
        control.enable();
      }
    }
  }

  /**
   * Gets the form control for this field
   */
  get formControl() {
    if (!this.form || !this.field) {
      return null;
    }
    return this.form.get(this.field.name);
  }

  /**
   * Gets CSS classes for the field
   */
  getFieldClasses(): string {
    if (!this.field) {
      return '';
    }
    return this.fieldRenderer.getFieldCssClasses(this.field);
  }

  /**
   * Gets Material form field appearance
   */
  getMatFieldAppearance(): string {
    return this.fieldRenderer.getMatFieldAppearance();
  }

  /**
   * Checks if current language is Arabic
   */
  isArabic(): boolean {
    return this.translateService.getActiveLang() === 'ar';
  }

  /**
   * Gets text direction based on language
   */
  getTextDirection(): string {
    return this.isArabic() ? 'rtl' : 'ltr';
  }

  /**
   * Handles date change for date/datetime fields
   */
  onDateChange(event: any): void {
    if (!this.field || (this.field.type !== 'date' && this.field.type !== 'datetime')) {
      return;
    }
    const value = event.target ? event.target.value : event.value;
    this.formControl?.setValue(value);
  }

  /**
   * Formats date value for display
   */
  formatDateValue(value: any): string {
    if (!this.field) {
      return value;
    }
    if (this.field.type === 'date') {
      return this.fieldRenderer.formatDateValue(value, 'date');
    } else if (this.field.type === 'datetime') {
      return this.fieldRenderer.formatDateValue(value, 'datetime');
    }
    return value;
  }

  /**
   * Gets the input type for HTML input elements
   */
  getInputType(): string {
    if (!this.field) {
      return 'text';
    }
    switch (this.field.type) {
      case 'number':
        return 'number';
      case 'date':
        return 'date';
      case 'datetime':
        return 'datetime-local';
      case 'text':
      default:
        return 'text';
    }
  }

  /**
   * Gets placeholder text with translation
   */
  getPlaceholder(): string {
    return this.field?.placeholder || '';
  }

  /**
   * Gets label text with translation
   */
  getLabel(): string {
    return this.field?.label || '';
  }

  /**
   * Checks if field has validation errors
   */
  hasError(): boolean {
    const control = this.formControl;
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /**
   * Gets error message for the field
   */
  getErrorMessage(): string {
    const control = this.formControl;
    if (!control || !control.errors) return '';

    if (control.errors['required']) {
      return `${this.getLabel()} is required`;
    }
    if (control.errors['pattern']) {
      return `${this.getLabel()} format is invalid`;
    }
    if (control.errors['minlength']) {
      const minLength = control.errors['minlength'].requiredLength;
      return `${this.getLabel()} must be at least ${minLength} characters`;
    }
    if (control.errors['maxlength']) {
      const maxLength = control.errors['maxlength'].requiredLength;
      return `${this.getLabel()} cannot exceed ${maxLength} characters`;
    }
    if (control.errors['min']) {
      const minValue = control.errors['min'].min;
      return `${this.getLabel()} must be at least ${minValue}`;
    }
    if (control.errors['max']) {
      const maxValue = control.errors['max'].max;
      return `${this.getLabel()} cannot exceed ${maxValue}`;
    }

    return 'Invalid value';
  }

  /**
   * Tracks option items for select fields
   */
  trackByOption(index: number, option: any): any {
    return option.value;
  }

  /**
   * Checks if the field is required
   */
  isRequired(): boolean {
    return this.field?.required || false;
  }

  /**
   * Handles value changes and emits the change event
   */
  onValueChange(value: any): void {
    if (!this.field) {
      return;
    }
    this.onEditCustomField.emit({
      fieldName: this.field.name,
      value: value,
    });
  }

  /**
   * Handles input changes for real-time validation
   */
  onInputChange(event: any): void {
    const value = event.target ? event.target.value : event.value;
    this.formControl?.setValue(value);

    // Trigger validation immediately
    this.formControl?.markAsTouched();
    this.formControl?.updateValueAndValidity();
  }

  /**
   * Gets the maxlength attribute for input fields based on validation rules
   */
  getMaxLength(): number | undefined {
    if (this.field?.validations && this.field.validations[this.field.name]) {
      const maxRule = this.field.validations[this.field.name].find((rule) =>
        rule.startsWith('max:'),
      );
      if (maxRule) {
        return Number(maxRule.split(':')[1]);
      }
    }
    return undefined;
  }

  /**
   * Gets the current character count for the field
   */
  getCurrentCharCount(): number {
    const control = this.formControl;
    return control?.value?.toString().length || 0;
  }

  /**
   * Checks if the field is approaching the character limit (80% of max)
   */
  isApproachingLimit(): boolean {
    const maxLength = this.getMaxLength();
    const currentCount = this.getCurrentCharCount();
    if (!maxLength) return false;

    return currentCount >= maxLength * 0.8 && currentCount < maxLength;
  }

  /**
   * Checks if the field has reached the character limit
   */
  hasReachedLimit(): boolean {
    const maxLength = this.getMaxLength();
    const currentCount = this.getCurrentCharCount();
    if (!maxLength) return false;

    return currentCount >= maxLength;
  }

  /**
   * Gets the character count message
   */
  getCharCountMessage(): string {
    const maxLength = this.getMaxLength();
    const currentCount = this.getCurrentCharCount();

    if (!maxLength) return '';

    if (this.hasReachedLimit()) {
      return `${currentCount}/${maxLength} - Maximum length reached!`;
    } else if (this.isApproachingLimit()) {
      return `${currentCount}/${maxLength} - Approaching limit`;
    } else {
      return `${currentCount}/${maxLength}`;
    }
  }

  /**
   * Gets the display value for read-only mode
   */
  getDisplayValue(): string {
    if (!this.field) {
      return 'N/A';
    }
    const value = this.field.currentValue;

    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }

    switch (this.field.type) {
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'datetime':
        return new Date(value).toLocaleString();
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'select':
        if (this.field.options && this.field.options.length > 0) {
          const option = this.field.options.find((opt) => opt.value === value);
          return option ? option.label : value;
        }
        return value;
      default:
        return value;
    }
  }

  /**
   * Gets the icon for boolean fields
   */
  getBooleanIcon(): string {
    if (!this.field) {
      return 'cancel';
    }
    return this.field.currentValue ? 'check_circle' : 'cancel';
  }

  /**
   * Gets the CSS class for boolean fields
   */
  getBooleanIconClass(): string {
    if (!this.field) {
      return 'text-red-500';
    }
    return this.field.currentValue ? 'text-green-500' : 'text-red-500';
  }
}
